/**
 * GeometryPanel — orchestrates the geometry pipeline workflow.
 *
 * Tabbed interface:
 *   Upload    → Drag-drop VSP3 or STEP/IGES files
 *   Assembly  → View bodies, label roles, define hinges
 *   Sweep     → Configure deflection sweep parameters
 *   Transfer  → Upload to Flow360 + track case submission
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Select,
  Tabs,
  Text,
} from "@radix-ui/themes";
import { AssemblyUploadZone } from "../../geometry/AssemblyUploadZone";
import ControlSurfacePanel from "../../geometry/ControlSurfacePanel";
import { DeflectionSweepForm } from "../../geometry/DeflectionSweepForm";
import { SectionPreview } from "../../geometry/SectionPreview";
import { GapCheckPanel } from "../../geometry/GapCheckPanel";
import { TransferStatus } from "../../geometry/TransferStatus";
import { useGeometryStore } from "../../stores/geometryStore";
import type { Assembly, CSPreviewParams, HingeSpec } from "../../api/geometry";
import * as geometryApi from "../../api/geometry";

interface Props {
  projectId: string;
}

export default function GeometryPanel({ projectId }: Props) {
  const store = useGeometryStore();
  const [activeTab, setActiveTab] = useState("upload");
  const [selectedSurface, setSelectedSurface] = useState<string | null>(null);
  const [csParams, setCsParams] = useState<CSPreviewParams | null>(null);

  // -- Upload callback --
  const handleAssemblyCreated = useCallback(
    (assembly: Assembly) => {
      store.setAssembly(assembly);
      setActiveTab("assembly");
    },
    [store],
  );

  // -- Assembly operations --
  const handleUpdateHinge = useCallback(
    async (surfaceName: string, hinge: HingeSpec) => {
      if (!store.assembly) return;
      try {
        await geometryApi.updateHinge(store.assembly.id, surfaceName, hinge);
        const updated = await geometryApi.getAssembly(store.assembly.id);
        store.setAssembly(updated);
      } catch (e) {
        store.setError(String(e));
      }
    },
    [store],
  );

  const handleRelabelBody = useCallback(
    async (name: string, role: string, csType?: string) => {
      if (!store.assembly) return;
      try {
        await geometryApi.relabelBody(store.assembly.id, name, role, csType);
        const updated = await geometryApi.getAssembly(store.assembly.id);
        store.setAssembly(updated);
      } catch (e) {
        store.setError(String(e));
      }
    },
    [store],
  );

  // -- Sweep --
  const handleCreateSweep = useCallback(
    async (surfaces: Record<string, number[]>, mode: "full_factorial" | "one_at_a_time" | "paired") => {
      if (!store.assembly) return;
      store.setLoading(true);
      try {
        const sweep = await geometryApi.createDeflectionSweep({
          assembly_id: store.assembly.id,
          surfaces,
          mode,
        });
        store.setSweep(sweep);
        setActiveTab("transfer");
      } catch (e) {
        store.setError(String(e));
      } finally {
        store.setLoading(false);
      }
    },
    [store],
  );

  const handleSubmitCases = useCallback(async () => {
    if (!store.assembly || !store.sweep) return;
    store.setLoading(true);
    try {
      // Rebuild surfaces from store
      const surfaces: Record<string, number[]> = {};
      for (const cs of store.assembly.bodies.filter(
        (b) => b.role === "control_surface",
      )) {
        const raw = store.surfaceAngles[cs.name] ?? "";
        surfaces[cs.name] = raw
          .split(/[,\s]+/)
          .map(Number)
          .filter((n) => !isNaN(n));
      }

      const result = await geometryApi.createDeflectionCases({
        assembly_id: store.assembly.id,
        surfaces,
        mode: store.sweepMode,
        base_case_config: {},
        thread_project_id: projectId,
      });
      store.setCaseResults(result);
    } catch (e) {
      store.setError(String(e));
    } finally {
      store.setLoading(false);
    }
  }, [store, projectId]);

  // -- Deflected geometry generation --
  const sweepPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (sweepPollRef.current) clearInterval(sweepPollRef.current);
    };
  }, []);

  const handleGenerateAll = useCallback(async () => {
    if (!store.assembly) return;
    store.setLoading(true);
    try {
      const surfaces: Record<string, number[]> = {};
      for (const cs of store.assembly.bodies.filter(
        (b) => b.role === "control_surface",
      )) {
        const raw = store.surfaceAngles[cs.name] ?? "";
        surfaces[cs.name] = raw
          .split(/[,\s]+/)
          .map(Number)
          .filter((n) => !isNaN(n));
      }

      const result = await geometryApi.generateDeflectedSweep(
        store.assembly.id,
        surfaces,
        store.sweepMode,
      );
      store.setSweepJobId(result.job_id);

      if (sweepPollRef.current) clearInterval(sweepPollRef.current);
      sweepPollRef.current = setInterval(async () => {
        try {
          const status = await geometryApi.getJobStatus(result.job_id);
          store.setSweepJobStatus(status);
          if (status.status === "complete" || status.status === "error") {
            if (sweepPollRef.current) clearInterval(sweepPollRef.current);
            sweepPollRef.current = null;
            store.setLoading(false);
          }
        } catch {
          if (sweepPollRef.current) clearInterval(sweepPollRef.current);
          sweepPollRef.current = null;
          store.setLoading(false);
        }
      }, 3000);
    } catch (e) {
      store.setError(String(e));
      store.setLoading(false);
    }
  }, [store]);

  const controlSurfaces =
    store.assembly?.bodies.filter((b) => b.role === "control_surface") ?? [];

  const sweepJobComplete = store.sweepJobStatus?.status === "complete";
  const sweepJobProgress = store.sweepJobStatus?.progress ?? 0;

  // Auto-select first CS when assembly changes
  const effectiveSurface = useMemo(() => {
    if (selectedSurface && controlSurfaces.some((cs) => cs.name === selectedSurface)) {
      return selectedSurface;
    }
    return controlSurfaces[0]?.name ?? null;
  }, [selectedSurface, controlSurfaces]);

  useEffect(() => {
    if (!store.assembly || !effectiveSurface) {
      setCsParams(null);
      return;
    }
    setCsParams({
      assembly_id: store.assembly.id,
      surface_name: effectiveSurface,
      eta1: 0.55,
      eta2: 0.95,
      xc1: 0.70,
      xc2: 0.70,
      yh1: 0.3,
      yh2: 0.3,
      gap_mm: 10,
      delta_deg: 15,
      section_eta: 0.5,
      fillet_mm: 0,
    });
  }, [store.assembly, effectiveSurface]);

  return (
    <Box>
      <Flex align="center" justify="between" mb="3">
        <Text size="3" weight="bold">
          Geometry Pipeline
        </Text>
        {store.assembly && (
          <Badge size="1" variant="soft" color="green">
            {store.assembly.name}
          </Badge>
        )}
      </Flex>

      {store.error && (
        <Card mb="2" style={{ background: "var(--red-2)" }}>
          <Text size="2" color="red">
            {store.error}
          </Text>
        </Card>
      )}

      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Trigger value="upload">Upload</Tabs.Trigger>
          <Tabs.Trigger value="assembly" disabled={!store.assembly}>
            Assembly
            {store.assembly && (
              <Badge size="1" variant="soft" color="blue" ml="1">
                {store.assembly.body_count}
              </Badge>
            )}
          </Tabs.Trigger>
          <Tabs.Trigger
            value="shape"
            disabled={!store.assembly || controlSurfaces.length === 0}
          >
            Shape
          </Tabs.Trigger>
          <Tabs.Trigger
            value="sweep"
            disabled={!store.assembly || controlSurfaces.length === 0}
          >
            Sweep
            {store.sweep && (
              <Badge size="1" variant="soft" color="green" ml="1">
                {store.sweep.case_count}
              </Badge>
            )}
          </Tabs.Trigger>
          <Tabs.Trigger value="transfer" disabled={!store.assembly}>
            Transfer
          </Tabs.Trigger>
        </Tabs.List>

        <Box pt="3">
          <Tabs.Content value="upload">
            <AssemblyUploadZone
              onAssemblyCreated={handleAssemblyCreated}
              projectId={projectId}
            />
            {store.assembly && (
              <Card mt="3" size="1">
                <Text size="2">
                  Loaded <strong>{store.assembly.name}</strong>:{" "}
                  {store.assembly.body_count} bodies,{" "}
                  {store.assembly.control_surface_count} control surfaces
                </Text>
                <Button
                  size="1"
                  variant="soft"
                  mt="2"
                  onClick={() => setActiveTab("assembly")}
                >
                  View Assembly
                </Button>
              </Card>
            )}
          </Tabs.Content>

          <Tabs.Content value="assembly">
            {store.assembly && (
              <ControlSurfacePanel
                assembly={store.assembly}
                onUpdateHinge={handleUpdateHinge}
                onRelabelBody={handleRelabelBody}
                onCreateSweep={handleCreateSweep}
                sweep={store.sweep}
                loading={store.loading}
              />
            )}
          </Tabs.Content>

          <Tabs.Content value="shape">
            {store.assembly && effectiveSurface && (
              <Flex direction="column" gap="3">
                {/* Surface selector (when multiple CS) */}
                {controlSurfaces.length > 1 && (
                  <Select.Root
                    value={effectiveSurface}
                    onValueChange={setSelectedSurface}
                  >
                    <Select.Trigger placeholder="Select surface" />
                    <Select.Content>
                      {controlSurfaces.map((cs) => (
                        <Select.Item key={cs.name} value={cs.name}>
                          {cs.name} ({cs.cs_type ?? "custom"})
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )}

                <SectionPreview
                  assembly={store.assembly}
                  surfaceName={effectiveSurface}
                  onParamsChange={setCsParams}
                />

                {csParams && (
                  <GapCheckPanel
                    assembly={store.assembly}
                    surfaceName={effectiveSurface}
                    csParams={csParams}
                  />
                )}
              </Flex>
            )}
          </Tabs.Content>

          <Tabs.Content value="sweep">
            <DeflectionSweepForm
              controlSurfaces={controlSurfaces}
              surfaceAngles={store.surfaceAngles}
              onSurfaceAnglesChange={store.setSurfaceAngles}
              mode={store.sweepMode}
              onModeChange={store.setSweepMode}
              onSubmit={handleCreateSweep}
              submitting={store.loading}
            />

            {store.sweep && (
              <Card mt="3" size="1">
                <Text size="2">
                  {store.sweep.case_count} cases ready.
                </Text>
                <Button
                  size="1"
                  variant="soft"
                  mt="2"
                  onClick={() => setActiveTab("transfer")}
                >
                  Proceed to Transfer
                </Button>
              </Card>
            )}
          </Tabs.Content>

          <Tabs.Content value="transfer">
            <Flex direction="column" gap="3">
              {/* Step 1: Generate all deflected STEPs */}
              <Card size="1">
                <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
                  Step 1: Generate Deflected Geometries
                </Text>
                <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
                  Generates one pre-rotated compound STEP per deflection case.
                </Text>
                <Flex gap="2" align="center">
                  <Button
                    size="2"
                    onClick={handleGenerateAll}
                    disabled={
                      store.loading ||
                      !store.assembly ||
                      controlSurfaces.length === 0
                    }
                    loading={store.loading && !sweepJobComplete}
                  >
                    Generate All
                  </Button>
                  {store.sweepJobStatus && (
                    <Text size="2" color="gray">
                      {store.sweepJobStatus.status === "processing"
                        ? store.sweepJobStatus.message
                        : store.sweepJobStatus.status === "complete"
                          ? `Complete — ${(store.sweepJobStatus.result as Record<string, number> | null)?.completed ?? 0} STEPs`
                          : store.sweepJobStatus.error ?? "Error"}
                    </Text>
                  )}
                </Flex>
                {store.sweepJobStatus?.status === "processing" && (
                  <Box
                    mt="2"
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: "var(--gray-4)",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      style={{
                        height: "100%",
                        width: `${sweepJobProgress * 100}%`,
                        background: "var(--blue-9)",
                        transition: "width 0.3s",
                      }}
                    />
                  </Box>
                )}
              </Card>

              {/* Step 2: Upload to Flow360 */}
              <Card size="1">
                <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
                  Step 2: Upload to Flow360
                </Text>
                <Text size="1" color="gray" mb="2" style={{ display: "block" }}>
                  Uploads each pre-rotated geometry and creates a Flow360 case per deflection.
                </Text>
                <Flex gap="2">
                  <Button
                    size="2"
                    onClick={handleSubmitCases}
                    disabled={store.loading || !sweepJobComplete}
                    loading={store.loading && sweepJobComplete}
                  >
                    Upload to Flow360
                  </Button>
                </Flex>
              </Card>

              <TransferStatus
                transferResult={store.transferResult}
                caseResults={store.caseResults}
              />
            </Flex>
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </Box>
  );
}
